<template>
  <TBody class="relative" data-id="data-table-results">
    <Tr
      class="datatable-table-row relative cursor-pointer hover:z-10 hover:shadow-md"
      v-for="(item, i) in items"
      :key="item.id"
      :class="{
        [item.classes || '']: true,
        'font-bold': notSeen(item),
        'bg-gray-50/[0.5]': seen(item),
      }"
      :data-id="`data-table-row-${item.id}`"
      :data-table-row="i.toString()"
      @click="onRowClick({ item, field: fields.find((it) => it.supplyTo) })"
    >
      <Td v-if="'favourite' in item">
        <Star
          :id="item.id"
          :enabled="item.favourite"
          :button="false"
          :class="item.favourite ? '' : muteUnlessHover"
          @toggle="onToggle"
        />
      </Td>
      <!-- eslint-disable-next-line -->
      <Td v-if="hasSlots" data-table-cell="slot" data-id="table-cell">
        <slot name="row" :item="item" :index="i" />
      </Td>
      <TableCell
        v-for="(field, j) in fields"
        :key="j"
        :field="field"
        :item="item"
      />
      <!-- actions -->
      <Td class="text-right" v-if="hasActions">
        <a
          class="text-link"
          v-if="!disableEdit"
          data-action="view"
          data-id="btn-edit"
          href="#"
          :title="'View'"
          @click.prevent.stop="onEdit({ item, field: { key: 'edit' } })"
        >
          <span class="sr-only">{{ 'View' }}</span>
          <Icon icon="view" />
        </a>
        <a
          class="ml-3 text-red-600"
          v-if="!disableDelete"
          data-action="delete"
          data-id="btn-delete"
          href="#"
          :title="'Delete'"
          @click.prevent.stop="onDelete(item)"
        >
          <span class="sr-only">{{ 'Delete' }}</span>
          <Icon icon="delete" />
        </a>
        <a
          class="ml-3 text-red-600"
          v-if="enableRedrive"
          data-action="redrive"
          data-id="btn-redrive"
          href="#"
          :title="'Resend'"
          @click.prevent.stop="onRedrive(item)"
        >
          <span class="sr-only">{{ 'Resend' }}</span>
          <Icon icon="redrive" />
        </a>
      </Td>
    </Tr>
    <tr>
      <td />
    </tr>
  </TBody>
</template>
<script setup lang="ts" generic="T extends DataTableItem">
import Icon from '~/components/form/Icon.vue';
import type { Field } from '~/lib/services/helpers';
import type { DataTableEditResult } from '~/lib/services/types';
import Star from '~/components/Star.vue';
import TableCell from '~/components/table/parts/TableCell.vue';
import { computed, useSlots } from 'vue';
import Tr from '~/components/table/parts/Tr.vue';
import Td from '~/components/table/parts/Td.vue';
import TBody from '~/components/table/parts/TBody.vue';
import type { DataTableItem, TogglePayload } from '~/lib/services/types';

withDefaults(
  defineProps<{
    hasActions?: boolean;
    enableRedrive?: boolean;
    disableEdit?: boolean;
    disableDelete?: boolean;
    items: T[];
    fields: Field<T>[];
  }>(),
  {},
);

const notSeen = (item: T) => {
  return item.read === false || item.seen === false;
};
const seen = (item: T) => {
  return item.read || item.seen;
};
// font-bold row classes for item read or seen false
const slots = useSlots();
const hasSlots = computed(() => !!slots.row);

const emit = defineEmits<{
  (e: 'edit', p: DataTableEditResult<T>): void;
  (e: 'delete', p: T): void;
  (e: 'redrive', p: T): void;
  (e: 'toggle', p: TogglePayload): void;
}>();

const onRowClick = (payload: DataTableEditResult<T>) => {
  emit('edit', payload);
};

const onDelete = (item: T) => {
  emit('delete', item);
};

const onRedrive = (item: T) => {
  emit('redrive', item);
};

const muteUnlessHover = 'text-gray-400 group-hover/tr:text-gray-600';

const onEdit = (payload: DataTableEditResult<T>) => {
  emit('edit', payload);
};

const onToggle = (payload: TogglePayload) => {
  emit('toggle', payload);
};
</script>
